import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getErrorMessage } from '../../lib/errors';
import { useAuthStore } from '../../stores/authStore';
import { useTranslation } from '../../i18n';
import { useNbTheme } from '../../hooks/useNbTheme';
import { useRole } from '../../hooks/useRole';
import CardNB from '../../components/ui-nb/CardNB';
import ButtonNB from '../../components/ui-nb/ButtonNB';
import { error as logError } from '../../lib/logger';

interface DashboardMetrics {
  orders_today: number;
  orders_delivered: number;
  active_technicians: number;
  avg_repair_time: string;
  pending_parts: number;
  delayed_deliveries: number;
}

interface TechMetrics {
  my_open: number;
  my_in_progress: number;
  received_today: number;
  my_branches: number;
}

interface MetricCardProps {
  title: string;
  children: React.ReactNode;
  onClick: () => void;
  mutedColor: string;
}

function MetricCard({ title, children, onClick, mutedColor }: MetricCardProps) {
  return (
    <CardNB variant="interactive" onClick={onClick} className="w-full">
      <h3 className="text-sm font-medium uppercase tracking-wide mb-3" style={{ color: mutedColor }}>{title}</h3>
      {children}
    </CardNB>
  );
}

export default function DashboardHome() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const navigate = useNavigate();
  const role = useRole();
  const organizationId = useAuthStore(s => s.user?.organizationId);
  const userId = useAuthStore(s => s.user?.id);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [techMetrics, setTechMetrics] = useState<TechMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { colors } = useNbTheme();

  const navLang = lang || 'en';

  const isAdmin = role === 'admin';

  useEffect(() => {
    if (!organizationId) return;

    async function fetchMetrics() {
      try {
        if (isAdmin) {
          const { data, error: rpcError } = await supabase.rpc('dashboard_metrics', {
            p_organization_id: organizationId,
          });
          if (rpcError) throw rpcError;
          if (data && data.length > 0) {
            setMetrics(data[0] as DashboardMetrics);
          } else {
            setMetrics(null);
          }
        } else {
          // Technician sees their own scoped metrics
          const now = new Date();
          const todayStr = now.toISOString().split('T')[0];

          const [openRes, inProgressRes, receivedRes, branchesRes] = await Promise.all([
            // My open orders (assigned to me, not delivered/ready)
            supabase.from('orders').select('id', { count: 'exact', head: true }).eq('assigned_to', userId).not('status', 'in', '("delivered","ready")'),
            // My in-progress
            supabase.from('orders').select('id', { count: 'exact', head: true }).eq('assigned_to', userId).eq('status', 'in progress'),
            // Received today at my branches
            supabase.from('orders').select('id', { count: 'exact', head: true }).gte('created_at', `${todayStr}T00:00:00`).eq('status', 'received'),
            // My branch count
            supabase.from('branch_user').select('branch_id', { count: 'exact', head: true }).eq('user_id', userId),
          ]);

          setTechMetrics({
            my_open: openRes.count ?? 0,
            my_in_progress: inProgressRes.count ?? 0,
            received_today: receivedRes.count ?? 0,
            my_branches: branchesRes.count ?? 0,
          });
        }
      } catch (err) {
        logError('Error fetching metrics', { error: getErrorMessage(err) });
        setError(getErrorMessage(err) || 'Failed to load metrics');
      } finally {
        setLoading(false);
      }
    }

    fetchMetrics();
  }, [organizationId, isAdmin, userId]);

  const m = metrics ?? { orders_today: 0, orders_delivered: 0, active_technicians: 0, avg_repair_time: '—', pending_parts: 0, delayed_deliveries: 0 };
  const tm = techMetrics ?? { my_open: 0, my_in_progress: 0, received_today: 0, my_branches: 0 };

  return (
    <>
      <h1 className="text-2xl font-bold mb-8" style={{ color: colors.textPrimary }}>{t('dashboard.title')}</h1>

      {error && (
        <div className="mb-6 px-4 py-3 text-sm" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {isAdmin ? (
          <>
          <MetricCard mutedColor={colors.textMuted} title={t('dashboard.ordersToday')} onClick={() => navigate(`/${navLang}/admin/orders`)}>
            <div className="flex items-end justify-between">
              <div>
                <span className="text-4xl font-bold" style={{ color: colors.textPrimary }}>{loading ? '—' : m.orders_today}</span>
                <p className="text-sm mt-1" style={{ color: colors.textMuted }}>{t('dashboard.ordersReceived')}</p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-semibold" style={{ color: colors.textSecondary }}>{loading ? '—' : m.orders_delivered}</span>
                <p className="text-xs" style={{ color: colors.textMuted }}>{t('dashboard.ordersDelivered')}</p>
              </div>
            </div>
          </MetricCard>

          <MetricCard mutedColor={colors.textMuted} title={t('dashboard.activeTechnicians')} onClick={() => navigate(`/${navLang}/admin/technicians`)}>
            <div>
              <span className="text-4xl font-bold" style={{ color: colors.textPrimary }}>{loading ? '—' : m.active_technicians}</span>
              <p className="text-sm mt-1" style={{ color: colors.textMuted }}>{t('dashboard.technicians')}</p>
            </div>
          </MetricCard>

          <MetricCard mutedColor={colors.textMuted} title={t('dashboard.avgRepairTime')} onClick={() => navigate(`/${navLang}/admin/reports`)}>
            <div>
              <span className="text-4xl font-bold" style={{ color: colors.textPrimary }}>{loading ? '—' : m.avg_repair_time}</span>
              <div className="mt-3 w-full h-2 bg-nb-white border-2 border-nb-black">
                <div className="h-full" style={{ width: '75%', backgroundColor: 'var(--nb-text-primary)' }} />
              </div>
              <p className="text-xs mt-2" style={{ color: colors.textMuted }}>{t('dashboard.vsGoal')}</p>
            </div>
          </MetricCard>

          <MetricCard mutedColor={colors.textMuted} title={t('dashboard.alerts')} onClick={() => navigate(`/${navLang}/admin/alerts`)}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: colors.textSecondary }}>{t('dashboard.pendingParts')}</span>
                <span className="text-lg font-bold" style={{ color: '#FFD600' }}>{loading ? '—' : m.pending_parts}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: colors.textSecondary }}>{t('dashboard.delayedDeliveries')}</span>
                <span className="text-lg font-bold" style={{ color: '#ef4444' }}>{loading ? '—' : m.delayed_deliveries}</span>
              </div>
            </div>
          </MetricCard>
          </>
        ) : (
          <>
          <MetricCard mutedColor={colors.textMuted} title="My Open Orders" onClick={() => navigate(`/${navLang}/admin/orders`)}>
            <div>
              <span className="text-4xl font-bold" style={{ color: colors.textPrimary }}>{loading ? '—' : tm.my_open}</span>
              <p className="text-sm mt-1" style={{ color: colors.textMuted }}>Assigned to me</p>
            </div>
          </MetricCard>

          <MetricCard mutedColor={colors.textMuted} title="In Progress" onClick={() => navigate(`/${navLang}/admin/orders`)}>
            <div>
              <span className="text-4xl font-bold" style={{ color: colors.textPrimary }}>{loading ? '—' : tm.my_in_progress}</span>
              <p className="text-sm mt-1" style={{ color: colors.textMuted }}>Being repaired</p>
            </div>
          </MetricCard>

          <MetricCard mutedColor={colors.textMuted} title="Received Today" onClick={() => navigate(`/${navLang}/admin/orders`)}>
            <div>
              <span className="text-4xl font-bold" style={{ color: colors.textPrimary }}>{loading ? '—' : tm.received_today}</span>
              <p className="text-sm mt-1" style={{ color: colors.textMuted }}>All branches</p>
            </div>
          </MetricCard>

          <MetricCard mutedColor={colors.textMuted} title="My Branches" onClick={() => navigate(`/${navLang}/admin/branches`)}>
            <div>
              <span className="text-4xl font-bold" style={{ color: colors.textPrimary }}>{loading ? '—' : tm.my_branches}</span>
              <p className="text-sm mt-1" style={{ color: colors.textMuted }}>Assigned to me</p>
            </div>
          </MetricCard>
          </>
        )}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <p className="text-sm" style={{ color: colors.textMuted }}>{t('dashboard.lastUpdate')}</p>
        <ButtonNB variant="secondary" size="sm" onClick={() => window.location.reload()}>
          {t('dashboard.refresh')}
        </ButtonNB>
      </div>
    </>
  );
}
